import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { kundenListe } from '../api';
import { Seitenkopf, StatusPille } from '../komponenten/Bausteine';
import KundeDialog from '../komponenten/KundeDialog';
import { datum, fehlertext, seit } from '../utils/format';

const FILTER = [
  ['alle', 'Alle', () => true],
  ['ablauf', 'Läuft bald ab', (k) => k.status === 'laeuft_bald_ab'],
  ['zu', 'Abgelaufen oder gesperrt', (k) => ['abgelaufen', 'gesperrt'].includes(k.status)],
  ['offen', 'Nicht aktiviert', (k) => k.status === 'nicht_aktiviert' || !k.lizenzen],
  ['test', 'Testkunden', (k) => k.ist_testkunde],
];

export default function Kunden() {
  const navigate = useNavigate();
  const [kunden, setKunden] = useState(null);
  const [fehler, setFehler] = useState('');
  const [suche, setSuche] = useState('');
  const [filter, setFilter] = useState('alle');
  const [neu, setNeu] = useState(false);

  useEffect(() => {
    kundenListe().then((r) => setKunden(r.data)).catch((e) => setFehler(fehlertext(e, 'Kunden konnten nicht geladen werden.')));
  }, []);

  const sichtbar = useMemo(() => {
    const worte = suche.toLowerCase().split(/\s+/).filter(Boolean);
    const passt = FILTER.find((f) => f[0] === filter)[2];
    return (kunden || []).filter((k) => {
      if (!passt(k)) return false;
      const heu = [k.anzeige, k.name, k.ansprechpartner, k.kontakt_email, k.telefon, k.notiz, k.version].filter(Boolean).join(' ').toLowerCase();
      return worte.every((w) => heu.includes(w));
    });
  }, [kunden, suche, filter]);

  return (
    <Box>
      <Seitenkopf titel="Kunden" untertitel="Jeder Kunde hat eine Akte mit Lizenz, Gerät, Installation und Verlauf.">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNeu(true)}>Neuer Kunde</Button>
      </Seitenkopf>
      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <TextField
          id="kunden-suche" placeholder="Name, Ansprechpartner, E-Mail oder Notiz suchen" value={suche}
          onChange={(e) => setSuche(e.target.value)} sx={{ flex: '1 1 260px', bgcolor: 'background.paper' }}
        />
        {FILTER.map(([id, text, passt]) => (
          <Chip
            key={id} label={`${text} ${(kunden || []).filter(passt).length}`} onClick={() => setFilter(id)}
            color={filter === id ? 'primary' : 'default'} variant={filter === id ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kunde</TableCell>
                <TableCell>Lizenz</TableCell>
                <TableCell>Gültig bis</TableCell>
                <TableCell>Zuletzt gemeldet</TableCell>
                <TableCell>Version</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sichtbar.map((k) => (
                <TableRow key={k.id} hover onClick={() => navigate(`/kunden/${k.id}`)} sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>
                      {k.anzeige}{k.ist_testkunde && <Chip size="small" label="Testkunde" sx={{ ml: 1 }} />}
                    </Typography>
                    {(k.ansprechpartner || k.kontakt_email) && (
                      <Typography variant="body2" color="text.secondary">{[k.ansprechpartner, k.kontakt_email].filter(Boolean).join(' · ')}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{k.lizenzen ? <StatusPille status={k.status} /> : <Typography variant="body2" color="text.secondary">keine Lizenz</Typography>}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{k.lizenzen ? (k.gueltig_bis ? datum(k.gueltig_bis) : 'unbefristet') : '–'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{k.letzter_kontakt ? seit(k.letzter_kontakt) : '–'}</TableCell>
                  <TableCell>{k.version || '–'}</TableCell>
                </TableRow>
              ))}
              {kunden && sichtbar.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                    {kunden.length === 0 ? 'Noch keine Kunden. Mit „Neuer Kunde“ den ersten anlegen.' : 'Kein Kunde passt zu Suche und Filter.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <KundeDialog offen={neu} kunde={null} onZu={() => setNeu(false)} onFertig={(k) => navigate(`/kunden/${k.id}?aktion=neu`)} />
    </Box>
  );
}
